import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Stethoscope, Mail, Lock, User, Phone, AlertCircle, Loader, UserCircle, Smartphone, CreditCard } from 'lucide-react';
import DisclaimerModal from '../../components/common/DisclaimerModal';
import { buildPatientDisclaimer, buildDoctorDisclaimer } from '../../config/content';
import usePricing from '../../hooks/usePricing';
import { FUNNEL, trackFunnelStep } from '../../services/analytics';
import useDoctorRegistrationOpen from '../../hooks/useDoctorRegistrationOpen';

function RegisterPage() {
  // Abrió el formulario. La distancia entre este paso y SIGN_UP mide cuánta
  // gente abandona el registro a medio llenar.
  useEffect(() => {
    trackFunnelStep(FUNNEL.BEGIN_REGISTRATION);
  }, []);

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    role: 'patient',
    firstName: '',
    lastName: '',
    phone: '',
    documentType: 'CC',
    documentNumber: '',
    nequiNumber: '',
    whatsappOptIn: false,
    acceptedTerms: false
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showDisclaimer, setShowDisclaimer] = useState(false);

  const { register } = useAuth();
  const navigate = useNavigate();
  // El admin abre y cierra el registro de médicos desde el panel. Cerrado, el
  // formulario solo registra pacientes (sin selector de rol).
  const { open: doctorRegistrationOpen } = useDoctorRegistrationOpen();

  const isDoctor = formData.role === 'doctor';
  const isPatient = formData.role === 'patient';
  // Las cláusulas de pago citan la tarifa vigente que define el backend.
  const { pricing } = usePricing();
  const disclaimer = isDoctor ? buildDoctorDisclaimer(pricing) : buildPatientDisclaimer(pricing);

  const handleChange = (e) => {
    const { name, type, value, checked } = e.target;
    // El número de Nequi solo admite dígitos (máx. 10).
    if (name === 'nequiNumber') {
      setFormData({ ...formData, nequiNumber: value.replace(/\D/g, '').slice(0, 10) });
      return;
    }
    // El número de documento solo admite dígitos (cédula colombiana).
    if (name === 'documentNumber') {
      setFormData({ ...formData, documentNumber: value.replace(/\D/g, '').slice(0, 15) });
      return;
    }
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  // Cambiar de rol reinicia la aceptación de términos (el disclaimer difiere por rol).
  const selectRole = (role) => {
    setFormData({ ...formData, role, acceptedTerms: false });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validaciones
    if (!formData.email || !formData.password || !formData.firstName || !formData.lastName) {
      setError('Por favor completa todos los campos obligatorios');
      return;
    }

    if (formData.password.length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Las contraseñas no coinciden');
      return;
    }

    if (!/^\d{6,15}$/.test(formData.documentNumber)) {
      setError('Ingresa un número de documento válido (solo dígitos)');
      return;
    }

    // Teléfono obligatorio y con formato válido para pacientes.
    if (isPatient && !/^\d{7,10}$/.test(formData.phone.trim())) {
      setError('Ingresa un número de teléfono válido (7 a 10 dígitos)');
      return;
    }

    if (!formData.acceptedTerms) {
      setError(
        isDoctor
          ? 'Debes aceptar los Términos y Condiciones para Profesionales de la Salud'
          : 'Debes aceptar los Términos y el Consentimiento Informado para continuar'
      );
      return;
    }

    if (isDoctor && !/^\d{10}$/.test(formData.nequiNumber)) {
      setError('Ingresa un número de Nequi válido (10 dígitos)');
      return;
    }

    setLoading(true);

    // Llamar al backend
    const result = await register({
      email: formData.email,
      password: formData.password,
      role: formData.role,
      firstName: formData.firstName,
      lastName: formData.lastName,
      phone: formData.phone,
      documentType: formData.documentType,
      documentNumber: formData.documentNumber,
      whatsappOptIn: formData.whatsappOptIn,
      acceptedTerms: formData.acceptedTerms,
      ...(isDoctor ? { nequiNumber: formData.nequiNumber } : {})
    });

    if (result.success) {
      // Registro completado: paso clave del embudo, con el rol para poder
      // separar pacientes de médicos en el informe.
      trackFunnelStep(FUNNEL.SIGN_UP, { role: formData.role });

      // Redirigir según el rol
      const route = result.user.role === 'doctor' ? '/dashboard/doctor' : '/dashboard/patient';
      navigate(route);
    } else {
      setError(result.message);
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-primary-100 flex items-center justify-center p-4 py-12">
      <div className="max-w-2xl w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center justify-center mb-6">
            <Stethoscope className="h-12 w-12 text-primary-600" />
            <span className="ml-2 text-3xl font-bold text-gray-900">DocEnLinea</span>
          </Link>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Crear Cuenta
          </h2>
          <p className="text-gray-600">
            Únete a la comunidad de telemedicina
          </p>
        </div>

        {/* Card de Registro */}
        <div className="bg-white rounded-xl shadow-xl p-8">
          {/* Error Alert */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start">
              <AlertCircle className="h-5 w-5 text-red-600 mt-0.5 mr-3 flex-shrink-0" />
              <div className="text-sm text-red-800">{error}</div>
            </div>
          )}

          {/* Formulario */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Selector de Rol (solo con el registro de médicos abierto) */}
            {doctorRegistrationOpen && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                ¿Cómo deseas usar DocEnLinea?
              </label>
              <div className="grid grid-cols-2 gap-4">
                <button
                  type="button"
                  onClick={() => selectRole('patient')}
                  className={`p-4 border-2 rounded-lg transition-all ${
                    formData.role === 'patient'
                      ? 'border-primary-600 bg-primary-50'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  <UserCircle className={`h-8 w-8 mx-auto mb-2 ${
                    formData.role === 'patient' ? 'text-primary-600' : 'text-gray-400'
                  }`} />
                  <div className="font-medium">Soy Paciente</div>
                  <div className="text-xs text-gray-500 mt-1">Busco atención médica</div>
                </button>
                
                <button
                  type="button"
                  onClick={() => selectRole('doctor')}
                  className={`p-4 border-2 rounded-lg transition-all ${
                    formData.role === 'doctor'
                      ? 'border-primary-600 bg-primary-50'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  <Stethoscope className={`h-8 w-8 mx-auto mb-2 ${
                    formData.role === 'doctor' ? 'text-primary-600' : 'text-gray-400'
                  }`} />
                  <div className="font-medium">Soy Médico</div>
                  <div className="text-xs text-gray-500 mt-1">Quiero ofrecer consultas</div>
                </button>
              </div>
            </div>
            )}

            {/* Nombre y Apellido */}
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-2">
                  Nombre *
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <User className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="firstName"
                    name="firstName"
                    type="text"
                    value={formData.firstName}
                    onChange={handleChange}
                    className="input-field pl-10"
                    placeholder="Juan"
                    disabled={loading}
                  />
                </div>
              </div>

              <div>
                <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-2">
                  Apellido *
                </label>
                <input
                  id="lastName"
                  name="lastName"
                  type="text"
                  value={formData.lastName}
                  onChange={handleChange}
                  className="input-field"
                  placeholder="Pérez"
                  disabled={loading}
                />
              </div>
            </div>

            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                Correo Electrónico *
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="input-field pl-10"
                  placeholder="tu@email.com"
                  disabled={loading}
                />
              </div>
            </div>

            {/* Teléfono */}
            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                {isPatient ? 'Teléfono *' : 'Teléfono (opcional)'}
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Phone className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="phone"
                  name="phone"
                  type="tel"
                  inputMode="numeric"
                  value={formData.phone}
                  onChange={handleChange}
                  className="input-field pl-10"
                  placeholder="3001234567"
                  disabled={loading}
                  required={isPatient}
                />
              </div>
              {isPatient && (
                <p className="mt-1 text-xs text-gray-500">
                  Lo usaremos para contactarte sobre tus citas.
                </p>
              )}
            </div>

            {/* Documento de identificación (todos los roles) */}
            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <label htmlFor="documentType" className="block text-sm font-medium text-gray-700 mb-2">
                  Tipo de documento *
                </label>
                <select
                  id="documentType"
                  name="documentType"
                  value={formData.documentType}
                  onChange={handleChange}
                  className="input-field"
                  disabled={loading}
                >
                  <option value="CC">Cédula de ciudadanía (CC)</option>
                  <option value="CE">Cédula de extranjería (CE)</option>
                  <option value="TI">Tarjeta de identidad (TI)</option>
                  <option value="PP">Pasaporte (PP)</option>
                </select>
              </div>
              <div className="md:col-span-2">
                <label htmlFor="documentNumber" className="block text-sm font-medium text-gray-700 mb-2">
                  Número de documento (cédula) *
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <CreditCard className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="documentNumber"
                    name="documentNumber"
                    type="tel"
                    inputMode="numeric"
                    value={formData.documentNumber}
                    onChange={handleChange}
                    className="input-field pl-10"
                    placeholder="1234567890"
                    disabled={loading}
                  />
                </div>
              </div>
            </div>

            {/* Número de Nequi (solo médicos) */}
            {isDoctor && (
              <div>
                <label htmlFor="nequiNumber" className="block text-sm font-medium text-gray-700 mb-2">
                  Número de Nequi (para recibir tus pagos) *
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Smartphone className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="nequiNumber"
                    name="nequiNumber"
                    type="tel"
                    inputMode="numeric"
                    value={formData.nequiNumber}
                    onChange={handleChange}
                    className="input-field pl-10"
                    placeholder="3001234567"
                    disabled={loading}
                  />
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  10 dígitos. Debe estar a tu nombre. Aquí recibirás el pago neto de cada consulta.
                </p>
              </div>
            )}

            {/* Opt-in WhatsApp */}
            <div className="flex items-start bg-green-50 border border-green-200 rounded-lg p-4">
              <input
                id="whatsappOptIn"
                name="whatsappOptIn"
                type="checkbox"
                checked={formData.whatsappOptIn}
                onChange={handleChange}
                disabled={loading}
                className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded mt-1"
              />
              <label htmlFor="whatsappOptIn" className="ml-2 block text-sm text-gray-700">
                Quiero recibir recordatorios de mis citas por WhatsApp
                <span className="block text-xs text-gray-500 mt-1">
                  Enviaremos un mensaje unos minutos antes de cada cita con el enlace directo a tu
                  consulta. Puedes cambiar esta preferencia cuando quieras desde tu perfil.
                </span>
              </label>
            </div>

            {/* Contraseñas */}
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                  Contraseña *
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="password"
                    name="password"
                    type="password"
                    value={formData.password}
                    onChange={handleChange}
                    className="input-field pl-10"
                    placeholder="••••••••"
                    disabled={loading}
                  />
                </div>
                <p className="mt-1 text-xs text-gray-500">Mínimo 6 caracteres</p>
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
                  Confirmar Contraseña *
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="confirmPassword"
                    name="confirmPassword"
                    type="password"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    className="input-field pl-10"
                    placeholder="••••••••"
                    disabled={loading}
                  />
                </div>
              </div>
            </div>

            {/* Términos / Consentimiento (según rol) */}
            <div className="flex items-start bg-gray-50 border border-gray-200 rounded-lg p-4">
              <input
                id="acceptedTerms"
                name="acceptedTerms"
                type="checkbox"
                checked={formData.acceptedTerms}
                onChange={handleChange}
                disabled={loading}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded mt-1"
              />
              <label htmlFor="acceptedTerms" className="ml-2 block text-sm text-gray-700">
                {isDoctor
                  ? 'He leído y acepto los '
                  : 'He leído y acepto los Términos y el '}
                <button
                  type="button"
                  onClick={() => setShowDisclaimer(true)}
                  className="text-primary-600 hover:text-primary-700 underline font-medium"
                >
                  {disclaimer.title}
                </button>
                {'.'}
                <span className="block text-xs text-gray-500 mt-1">
                  Haz clic en «Ver términos» para leer el texto completo.
                </span>
              </label>
            </div>

            {/* Botón Submit */}
            <button
              type="submit"
              disabled={loading}
              className="w-full btn-primary py-3 flex items-center justify-center"
            >
              {loading ? (
                <>
                  <Loader className="animate-spin h-5 w-5 mr-2" />
                  Creando cuenta...
                </>
              ) : (
                'Crear Cuenta'
              )}
            </button>
          </form>

          {/* Link a Login */}
          <div className="mt-6 text-center">
            <span className="text-sm text-gray-600">¿Ya tienes cuenta? </span>
            <Link
              to="/login"
              className="text-sm text-primary-600 hover:text-primary-700 font-medium"
            >
              Iniciar sesión
            </Link>
          </div>
        </div>

        {/* Link volver */}
        <div className="mt-6 text-center">
          <Link to="/" className="text-sm text-gray-600 hover:text-gray-900">
            ← Volver al inicio
          </Link>
        </div>
      </div>

      <DisclaimerModal
        open={showDisclaimer}
        onClose={() => setShowDisclaimer(false)}
        disclaimer={disclaimer}
      />
    </div>
  );
}

export default RegisterPage;
